import React, { useState, useEffect } from "react";
import { useTranslate } from "./useTranslate";
import dayjs from "dayjs";
import { CAMPAIGN_NAME_SEPARATOR } from "../constants";

// build full campaign name from string and vice versa

const useCampaignName = (defaultValue: string) => {
  const { t } = useTranslate();

  // [adv name] | [brand name] — [custom name]
  const [fullCampaignName, setFullCampaignName] = useState(defaultValue);

  // [custom name]
  const [customCampaignName, setCustomCampaignName] = useState("");

  // [adv name] | [brand name] —
  const [fullCampaignNameWithoutCustom, setFullCampaignNameWithoutCustom] =
    useState("");

  // check if advertiser and brand names are the same
  const getAdvertiserName = (advertiserName: string, brandName: string) => {
    if (advertiserName && brandName) {
      return advertiserName === brandName
        ? ""
        : advertiserName + ` ${CAMPAIGN_NAME_SEPARATOR} `;
    } else if (advertiserName) {
      return advertiserName + ` ${CAMPAIGN_NAME_SEPARATOR} `;
    } else {
      return `[${t`Advertiser Name`}] ${CAMPAIGN_NAME_SEPARATOR} `;
    }
  };

  const calcCampaignNameForSubmission = (
    advertiser: string,
    brand: string,
    broadcastingDate: string
  ) => {
    // if custom name is empty, use only advertiser and brand names
    if (!customCampaignName || customCampaignName === t`[Custom name]`) {
      return `${dayjs(broadcastingDate).format(
        "YYYY-MM"
      )} ${CAMPAIGN_NAME_SEPARATOR} ${getAdvertiserName(advertiser, brand)} ${
        brand || `[${t`Brand Name`}]`
      }`;
    } else {
      return fullCampaignName;
    }
  };

  useEffect(() => {
    const isFullCampaignName =
      defaultValue && defaultValue.includes(CAMPAIGN_NAME_SEPARATOR);

    if (isFullCampaignName) {
      // gets data from initial value
      const [nameData, customName] = defaultValue
        .split("—")
        .map((value) => value.trim());
      const [advertiserName, brandName] = nameData
        .split(CAMPAIGN_NAME_SEPARATOR)
        .map((value) => value.trim());

      // build full campaign name
      setFullCampaignName(
        `${getAdvertiserName(advertiserName, brandName)} ${
          brandName || `[${t`Brand Name`}]`
        } — ${customName || t`[Custom name]`}`
      );

      // sets custom campaign name
      setCustomCampaignName(customName?.slice(0, 59) || t`[Custom name]`);

      // sets full campaign name without custom name
      setFullCampaignNameWithoutCustom(
        `${getAdvertiserName(advertiserName, brandName)} ${
          brandName || `[${t`Brand Name`}]`
        }`
      );
    } else {
      setFullCampaignName(
        `${t`Advertiser Name`}] ${CAMPAIGN_NAME_SEPARATOR} [${t`Brand Name`}] — ${defaultValue}`
      );
      setCustomCampaignName(defaultValue);
      setFullCampaignNameWithoutCustom(
        `[${t`Advertiser Name`}] ${CAMPAIGN_NAME_SEPARATOR} [${t`Brand Name`}]`
      );
    }
  }, [defaultValue]);

  useEffect(() => {
    // watch for custom campaign name changes and changes full campaign name accordingly
    setFullCampaignName(
      fullCampaignNameWithoutCustom + " — " + customCampaignName
    );
  }, [customCampaignName]);

  const setCampaignName = (
    advertiser: string,
    brand: string,
    customName: string,
    broadcastingDate: string
  ) => {
    setFullCampaignName(
      `${
        dayjs(broadcastingDate).isValid()
          ? dayjs(broadcastingDate).format("YYYY-MM")
          : `[${t`Broadcasting date`}]`
      } ${CAMPAIGN_NAME_SEPARATOR} ${getAdvertiserName(advertiser, brand)} ${
        brand || `[${t`Brand Name`}]`
      } — ${customName || customCampaignName || t`[Custom name]`}`
    );
    setCustomCampaignName(
      (customName || customCampaignName || t`[Custom name]`)
    );
    setFullCampaignNameWithoutCustom(
      `${
        dayjs(broadcastingDate).isValid()
          ? dayjs(broadcastingDate).format("YYYY-MM")
          : `[${t`Broadcasting date`}]`
      } ${CAMPAIGN_NAME_SEPARATOR} ${getAdvertiserName(advertiser, brand)} ${
        brand || `[${t`Brand Name`}]`
      }`
    );
  };

  return {
    fullCampaignName,
    customCampaignName,
    fullCampaignNameWithoutCustom: fullCampaignNameWithoutCustom.slice(0, 59),
    setCampaignName,
    setCustomCampaignName,
    calcCampaignNameForSubmission,
  };
};

export default useCampaignName;
